/* -------------------------- SearchableSelect (custom) -------------------------- */

import { useEffect, useMemo, useRef, useState } from "react";

/* Why custom? Native <select> adds its own arrow, causing the “two chevrons” issue.
   This component uses a button + menu so there’s only ONE caret that we control. */

const SearchableSelect = ({
  label, // string shown when value === "All"
  options, // array of strings
  value, // selected string or "All"
  onChange, // (val) => void
  className = "", // parent column sets width; this is just for styling hooks
  buttonClassName = "", // for layout (we make it look like a form-control)
}) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hoverIndex, setHoverIndex] = useState(-1);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Filter options by query
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return options;
    return options.filter((o) => o.toLowerCase().includes(term));
  }, [options, q]);

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Focus the search box when opened
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    } else {
      setQ("");
      setHoverIndex(-1);
    }
  }, [open]);

  // Keyboard navigation
  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHoverIndex((i) => Math.min(i + 1, filtered.length - 1));
      scrollToHover();
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHoverIndex((i) => Math.max(i - 1, 0));
      scrollToHover();
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const pick = filtered[hoverIndex];
      if (typeof pick === "string") {
        onChange(pick);
        setOpen(false);
      }
    }
  };

  const scrollToHover = () => {
    if (!listRef.current) return;
    const nodes = listRef.current.querySelectorAll('[role="option"]');
    if (!nodes.length) return;
    const idx = Math.max(0, Math.min(hoverIndex, nodes.length - 1));
    const node = nodes[idx];
    if (node && node.scrollIntoView) {
      // only-if-needed prevents jumpiness
      node.scrollIntoView({ block: "nearest", inline: "nearest" });
    }
  };

  const currentLabel = value === "All" ? label : value;

  return (
    <div
      ref={wrapRef}
      className={`position-relative ${className}`}
      onKeyDown={onKeyDown}
    >
      {/* Button styled as a form-control but without native <select> arrow */}
      <button
        type="button"
        className={`btn bg-white border rounded-2 w-100 text-start d-flex align-items-center justify-content-between ${buttonClassName}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="text-truncate">{currentLabel}</span>
        {/* Single caret we fully control */}
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          aria-hidden="true"
          className={`ms-2 ${open ? "rotate-180" : ""}`}
        >
          <path
            fill="currentColor"
            d="M4.646 6.646a.5.5 0 0 1 .708 0L8 9.293l2.646-2.647a.5.5 0 0 1 .708.708L8.354 10.354a.5.5 0 0 1-.708 0L4.646 7.354a.5.5 0 0 1 0-.708z"
          />
        </svg>
      </button>

      {/* Popover menu */}
      {open && (
        <div
          className="shadow-sm border bg-white rounded-2 mt-1 p-2"
          style={{
            position: "absolute",
            zIndex: 1051,
            minWidth: "100%",
            maxHeight: 280,
            width: "100%",
          }}
        >
          <input
            ref={inputRef}
            type="text"
            className="form-control form-control-sm mb-2"
            placeholder="Type to search…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setHoverIndex(0);
            }}
          />

          {/* “All …” option */}
          <div
            role="option"
            tabIndex={-1}
            className={`px-2 py-1 rounded-1 mb-1 ${
              value === "All" ? "bg-primary text-white" : "option-hover"
            }`}
            onMouseEnter={() => setHoverIndex(-1)}
            onClick={() => {
              onChange("All");
              setOpen(false);
            }}
          >
            {label}
          </div>

          {/* Options list */}
          <div
            ref={listRef}
            role="listbox"
            style={{
              maxHeight: 160,
              overflowY: "auto",
              boxSizing: "border-box",
              paddingRight: 8,
            }}
          >
            {filtered.length ? (
              filtered.map((opt, i) => {
                const isActive = opt === value;
                const isHover = i === hoverIndex;
                return (
                  <div
                    key={opt}
                    role="option"
                    tabIndex={-1}
                    className={`px-2 py-1 rounded-1 mb-1 ${
                      isActive
                        ? "bg-primary text-white"
                        : isHover
                        ? "bg-light"
                        : "option-hover"
                    }`}
                    onMouseEnter={() => setHoverIndex(i)}
                    onClick={() => {
                      onChange(opt);
                      setOpen(false);
                    }}
                  >
                    {opt}
                  </div>
                );
              })
            ) : (
              <div className="text-muted small px-2 py-1">No matches</div>
            )}
          </div>
        </div>
      )}

      {/* tiny styles for hover + caret rotate */}
      <style>{`
        .option-hover { cursor: pointer; }
        .option-hover:hover { background: #f6f7f9; }
        .rotate-180 { transform: rotate(180deg); transition: transform .12s; }
      `}</style>
    </div>
  );
};

export default SearchableSelect;
